import {
    ChangeDetectionStrategy,
    Component,
    computed,
    model,
    ModelSignal,
    OnDestroy,
    OnInit,
    signal,
    Signal,
    WritableSignal,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { RouterService } from "../../../../../shared/_services/router.service";
import {
    Exchange,
    SymbolMarketInfo,
    TradingSymbol,
    TradingSymbolGroup,
    TradingSymbolsService,
    TradingSymbolType,
} from "./../trading-symbols.service";
import { CommandResult } from "../../../../../shared/_types/command-result.type";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";
import { ActivatedRoute } from "@angular/router";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-edit-trading-symbol",
    templateUrl: "./edit-trading-symbol.component.html",
    styleUrl: "./edit-trading-symbol.component.scss",
    standalone: false,
})
export class EditTradingSymbolComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    groups: TradingSymbolGroup[] = [];
    exchanges: Exchange[] = [];

    tradingSymbolId: ModelSignal<string> = model<string>("");
    tradingSymbolSymbol: ModelSignal<string> = model<string>("");
    tradingSymbolCompanyName: ModelSignal<string> = model<string>("");
    tradingSymbolIcon: ModelSignal<string | null> = model<string | null>("");
    tradingSymbolType: ModelSignal<TradingSymbolType> = model<TradingSymbolType>(TradingSymbolType.Undefined);
    tradingSymbolTradingGroupId: ModelSignal<string> = model<string>("");
    tradingSymbolExchangeId: ModelSignal<string> = model<string>("");
    tradingSymbolIsActive: ModelSignal<boolean> = model<boolean>(false);
    tradingSymbolDescription: ModelSignal<string | null> = model<string | null>("");
    tradingSymbolComment: ModelSignal<string | null> = model<string | null>("");

    isNew: WritableSignal<boolean> = signal(true);

    isSymbolLoaded: WritableSignal<boolean> = signal(false);
    isGroupsLoaded: WritableSignal<boolean> = signal(false);
    isExchangesLoaded: WritableSignal<boolean> = signal(false);

    isLoaded: Signal<boolean> = computed(
        () => this.isSymbolLoaded() && this.isGroupsLoaded() && this.isExchangesLoaded(),
    );

    isValid: Signal<boolean> = computed(
        () =>
            this.tradingSymbolSymbol() !== "" &&
            this.tradingSymbolCompanyName() !== "" &&
            this.tradingSymbolExchangeId() !== "" &&
            this.tradingSymbolTradingGroupId() !== "" &&
            this.tradingSymbolType() !== TradingSymbolType.Undefined,
    );

    TradingSymbolType = TradingSymbolType;

    constructor(
        private router: RouterService,
        private route: ActivatedRoute,
        private tradingSymbolsService: TradingSymbolsService,
        private notificationsService: NotificationsService,
    ) {}

    ngOnInit(): void {
        this.tradingSymbolsService
            .getTradingSymbolGroups()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((groups) => {
                this.groups = groups;
                this.isGroupsLoaded.set(true);
            });
        this.tradingSymbolsService
            .getExchanges()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((exchanges) => {
                this.exchanges = exchanges;
                this.isExchangesLoaded.set(true);
            });

        const id = this.route.snapshot.paramMap.get("id");
        if (id === "new") {
            this.isSymbolLoaded.set(true);
            return;
        }
        this.isNew.set(false);
        this.tradingSymbolsService
            .get(id!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (data: TradingSymbol) => {
                    this.tradingSymbolId.set(data.id);
                    this.tradingSymbolSymbol.set(data.symbol);
                    this.tradingSymbolCompanyName.set(data.companyName);
                    if (data.icon) {
                        this.tradingSymbolIcon.set(data.icon);
                    }
                    this.tradingSymbolType.set(data.type);
                    this.tradingSymbolTradingGroupId.set(data.tradingGroupId);
                    this.tradingSymbolExchangeId.set(data.exchangeId);
                    this.tradingSymbolIsActive.set(data.isActive);
                    if (data.description) {
                        this.tradingSymbolDescription.set(data.description);
                    }
                    if (data.comment) {
                        this.tradingSymbolComment.set(data.comment);
                    }

                    this.isSymbolLoaded.set(true);
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to load trading symbol");
                    this.router.navigateBack();
                },
            });
    }

    get tradingSymbolTypes(): TradingSymbolType[] {
        return Object.values(TradingSymbolType).filter((value) => typeof value === "number") as TradingSymbolType[];
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    loadSymbolInfo(): void {
        this.tradingSymbolsService
            .getMarketInfo(this.tradingSymbolSymbol())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (data: SymbolMarketInfo) => {
                    if (!data.isSuccess || !data.result) {
                        this.notificationsService.showError("Error", data.error ?? "Failed to load market info");
                        return;
                    }

                    this.tradingSymbolCompanyName.set(data.result.companyName);
                    this.tradingSymbolExchangeId.set(data.result.exchangeId);
                    if (data.result.icon) {
                        this.tradingSymbolIcon.set(data.result.icon);
                    }
                    this.tradingSymbolType.set(data.result.type);
                    if (data.result.description) {
                        this.tradingSymbolDescription.set(data.result.description);
                    }
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to load symbol info");
                },
            });
    }

    tradingSymbol(): TradingSymbol {
        return {
            id: this.tradingSymbolId(),
            symbol: this.tradingSymbolSymbol(),
            companyName: this.tradingSymbolCompanyName(),
            icon: this.tradingSymbolIcon(),
            type: this.tradingSymbolType(),
            tradingGroupId: this.tradingSymbolTradingGroupId(),
            exchangeId: this.tradingSymbolExchangeId(),
            isActive: this.tradingSymbolIsActive(),
            description: this.tradingSymbolDescription(),
            comment: this.tradingSymbolComment(),
        };
    }

    create(): void {
        this.tradingSymbolsService
            .create(this.tradingSymbol())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res: CommandResult) => {
                if (!res.success) {
                    this.notificationsService.showError("Error", res.error);
                    return;
                }
                this.notificationsService.showSuccess("Success", "Trading symbol created successfully");
                this.router.navigate(`/trading-symbols`);
            });
    }

    update(): void {
        this.tradingSymbolsService
            .update(this.tradingSymbol().id, this.tradingSymbol())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res: CommandResult) => {
                if (!res.success) {
                    this.notificationsService.showError("Error", res.error);
                    return;
                }
                this.notificationsService.showSuccess("Success", "Trading symbol updated successfully");
                this.router.navigate(`/trading-symbols`);
            });
    }

    cancel(): void {
        this.router.navigate(`/trading-symbols`);
    }
}
